import type { LogChange, LogDetail } from "@lift-admin/utils/logChangeFormatter";

type Peer = {
  id?: any;
  name?: string;
  display_order?: number;
};

const ORDER_LABELS = [
  "first",
  "second",
  "third",
  "fourth",
  "fifth",
  "sixth",
  "seventh",
  "eighth",
  "ninth",
  "tenth",
] as const;

/** Humanize 0-based display_order (matches channels table labels). */
export function formatOrderIndex(order: any): string {
  const n = Number(order);
  const i = Number.isFinite(n) ? n : 0;
  if (i >= 0 && i < ORDER_LABELS.length) return ORDER_LABELS[i];
  return `${i + 1}th`;
}

export function resolvePeerName(
  peerId: any,
  peers: Peer[] = [],
  fallbackLabel = "Item"
): string {
  if (peerId == null || peerId === "") return "Unknown";
  const peer = (peers || []).find((p) => String(p.id) === String(peerId));
  const name = peer?.name && String(peer.name).trim();
  return name || `${fallbackLabel} ${peerId}`;
}

/** Infer which peer sits immediately above the current item. */
export function inferBelowItemId(
  currentId: any,
  displayOrder: number,
  peers: Peer[] = []
): string | null {
  if (!Number.isFinite(Number(displayOrder)) || Number(displayOrder) === 0) {
    return null;
  }

  const sorted = [...(peers || [])].sort(
    (a, b) => (Number(a.display_order) || 0) - (Number(b.display_order) || 0)
  );
  const idx = sorted.findIndex((p) => String(p.id) === String(currentId));
  if (idx > 0) return String(sorted[idx - 1].id);

  const above = sorted.find(
    (p) =>
      String(p.id) !== String(currentId) &&
      (Number(p.display_order) || 0) === Number(displayOrder) - 1
  );
  return above?.id != null ? String(above.id) : null;
}

export function formatDisplayPositionLabel(opts: {
  makeItTop: boolean;
  belowItemId?: string | null;
  peers?: Peer[];
  displayOrder?: number | null;
  peerLabel?: string;
}): string {
  const {
    makeItTop,
    belowItemId,
    peers = [],
    displayOrder,
    peerLabel = "Item",
  } = opts;

  if (makeItTop) {
    return `Make it Top (${formatOrderIndex(0)})`;
  }

  if (belowItemId) {
    const name = resolvePeerName(belowItemId, peers, peerLabel);
    const order =
      displayOrder != null && Number.isFinite(Number(displayOrder))
        ? Number(displayOrder)
        : (() => {
            const peer = peers.find(
              (p) => String(p.id) === String(belowItemId)
            );
            return peer?.display_order != null
              ? Number(peer.display_order) + 1
              : null;
          })();
    return order != null
      ? `Make it Below "${name}" (${formatOrderIndex(order)})`
      : `Make it Below "${name}"`;
  }

  if (displayOrder != null && Number.isFinite(Number(displayOrder))) {
    const order = Number(displayOrder);
    if (order === 0) return `Make it Top (${formatOrderIndex(0)})`;
    return `Order: ${formatOrderIndex(order)}`;
  }

  return "Not Set";
}

/** Update log row for Make it Top / Make it Below position changes. */
export function buildDisplayPositionChange(args: {
  previousOrder: number;
  nextOrder: number;
  nextMakeItTop: boolean;
  nextBelowItemId?: string | null;
  peers: Peer[];
  currentId?: any;
  previousBelowItemId?: string | null;
  previousMakeItTop?: boolean;
  peerLabel?: string;
}): LogChange | null {
  const prevTop =
    args.previousMakeItTop ?? Number(args.previousOrder) === 0;
  const nextTop = Boolean(args.nextMakeItTop);

  const prevBelow =
    args.previousBelowItemId ??
    (prevTop
      ? null
      : inferBelowItemId(args.currentId, args.previousOrder, args.peers));
  const nextBelow = nextTop ? null : args.nextBelowItemId || null;

  // Only log when placement intent changes (Top vs Below X).
  // Ignore ordinal-only drift (e.g. third → second) when still below the same item —
  // that happens when display_order is recalculated on save without a UI position edit.
  const samePlacement =
    prevTop === nextTop &&
    (prevTop || String(prevBelow ?? "") === String(nextBelow ?? ""));
  if (samePlacement) return null;

  const from = formatDisplayPositionLabel({
    makeItTop: prevTop,
    belowItemId: prevBelow,
    peers: args.peers,
    displayOrder: args.previousOrder,
    peerLabel: args.peerLabel,
  });
  const to = formatDisplayPositionLabel({
    makeItTop: nextTop,
    belowItemId: nextBelow,
    peers: args.peers,
    displayOrder: args.nextOrder,
    peerLabel: args.peerLabel,
  });

  if (from === to) return null;
  return { label: "Display Position", from, to };
}

/** Create/delete detail row for display position. */
export function buildDisplayPositionDetail(opts: {
  makeItTop: boolean;
  belowItemId?: string | null;
  peers?: Peer[];
  displayOrder?: number | null;
  peerLabel?: string;
}): LogDetail {
  return {
    label: "Display Position",
    value: formatDisplayPositionLabel(opts),
  };
}
